<script lang="ts">
  import { tick, type ComponentProps, type Snippet } from "svelte"
  import { controlContext } from "@kontextwerk/control-web-frame/shared/logic/authentication"
  import BoardViewport from "./_modules/board-viewport"
  import CalendarOverview from "./_modules/calendar-overview"
  import CalendarViewControls from "./_modules/calendar-view-controls"
  import { projectTimelineColumns } from "../../_logic/column-projection"
  import { buildCalendarDaySummary } from "../../_logic/day-summary"
  import {
    CALENDAR_GRID_MINUTES,
    calendarScale,
    calendarTimeSlots,
    type CalendarGridMinutes,
    type CalendarView,
  } from "./_shared/_logic/display-scale"
  import {
    browserCalendarStorage,
    calendarPreferenceKey,
    DEFAULT_CALENDAR_PREFERENCES,
    readCalendarPreferences,
    saveCalendarPreferences,
  } from "./_logic/viewPreferences"
  import type { CalendarDisplayControls } from "./_logic/displayControls"

  type Props = ComponentProps<typeof BoardViewport> & {
    organizationId: string
    timezone: string
    visibleDays: Date[]
    presentation?: { view: CalendarView; gridMinutes: CalendarGridMinutes }
    /** Lets the page place controls around the calendar without taking over its state. */
    frame?: Snippet<[CalendarDisplayControls, Snippet]>
  }
  let {
    organizationId,
    timezone,
    visibleDays,
    presentation,
    frame,
    columns,
    layout,
    timeSlots,
    timeColumnHeight: _height,
    ...boardProps
  }: Props = $props()
  let preferences = $state({ ...DEFAULT_CALENDAR_PREFERENCES })
  let root: HTMLDivElement
  let controlsHeight = $state(60)
  const preferenceKey = $derived(
    // Display preferences belong to the signed-in browser identity.
    calendarPreferenceKey(organizationId, $controlContext?.actor.id ?? "anonymous"),
  )
  $effect(() => {
    if (presentation) return

    preferences = readCalendarPreferences(preferenceKey, browserCalendarStorage())
  })
  const displayView = $derived(presentation?.view ?? preferences.view)
  const displayGridMinutes = $derived(presentation?.gridMinutes ?? preferences.gridMinutes)
  const displayedControlsHeight = $derived(presentation || frame ? 0 : controlsHeight)
  const displayControls = $derived<CalendarDisplayControls>({
    view: displayView,
    gridMinutes: displayGridMinutes,
    gridOptions: CALENDAR_GRID_MINUTES,
    onViewChange: (view) => void changePreferences({ view }),
    onGridChange: (gridMinutes) => void changePreferences({ gridMinutes }),
  })
  const displayLayout = $derived({ ...layout, ...calendarScale(displayGridMinutes) })
  const endHour = $derived((timeSlots.at(-1)?.value ?? layout.dayStartHour * 60) / 60)
  const displaySlots = $derived(calendarTimeSlots(layout.dayStartHour, endHour, displayLayout))
  const displayHeight = $derived((displaySlots.length - 1) * displayLayout.slotHeight)
  const balancedColumns = $derived(
    columns.map((column) => ({
      ...column,
      daySummary: boardProps.isLoading ? null : buildCalendarDaySummary(column, timezone),
    })),
  )
  const displayColumns = $derived(
    projectTimelineColumns(balancedColumns, { ...displayLayout, dayEndHour: endHour }),
  )

  async function changePreferences(change: {
    view?: CalendarView
    gridMinutes?: CalendarGridMinutes
  }) {
    // Keep the part of the timeline currently at the top of its scroll container in view.
    let scrollParent: HTMLElement | null = root?.parentElement ?? null
    while (scrollParent && !/(auto|scroll)/.test(getComputedStyle(scrollParent).overflowY))
      scrollParent = scrollParent.parentElement
    const before = root?.getBoundingClientRect().top ?? 0
    const horizontalScroll =
      root?.querySelector<HTMLElement>(".board-scroll-window")?.scrollLeft ?? 0
    const viewportTop = scrollParent?.getBoundingClientRect().top ?? 0
    const sourceDay =
      preferences.view === "overview"
        ? Array.from(root?.querySelectorAll<HTMLElement>("[data-overview-day]") ?? []).find(
            (element) =>
              element.getBoundingClientRect().bottom > viewportTop + displayedControlsHeight,
          )?.dataset.overviewDay
        : Array.from(
            root?.querySelectorAll<HTMLElement>('[data-calendar-area="events"]') ?? [],
          ).find((element) => {
            const rect = element.getBoundingClientRect()

            return (
              rect.right > root.getBoundingClientRect().left &&
              rect.left < root.getBoundingClientRect().right
            )
          })?.dataset.dayTs
    const previousHeight = displayHeight
    const fraction =
      previousHeight > 0
        ? Math.max(0, (scrollParent?.getBoundingClientRect().top ?? 0) - before) / previousHeight
        : 0
    preferences = { ...preferences, ...change }
    saveCalendarPreferences(preferenceKey, preferences, browserCalendarStorage())
    await tick()

    if (scrollParent && change.gridMinutes && fraction > 0)
      scrollParent.scrollTop += fraction * (displayHeight - previousHeight)

    const timeline = root?.querySelector<HTMLElement>(".board-scroll-window")

    if (change.gridMinutes && timeline) timeline.scrollLeft = horizontalScroll

    if (change.view && sourceDay) {
      const date = sourceDay.includes("-")
        ? new Date(`${sourceDay}T12:00:00`)
        : new Date(Number(sourceDay))
      const dateKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
      const target =
        preferences.view === "overview"
          ? Array.from(root?.querySelectorAll<HTMLElement>("[data-overview-day]") ?? []).find(
              (element) => element.dataset.overviewDay === dateKey,
            )
          : Array.from(
              root?.querySelectorAll<HTMLElement>('[data-calendar-area="events"]') ?? [],
            ).find((element) => {
              const day = new Date(Number(element.dataset.dayTs))

              return (
                day.getFullYear() === date.getFullYear() &&
                day.getMonth() === date.getMonth() &&
                day.getDate() === date.getDate()
              )
            })

      if (target && before < viewportTop)
        target.scrollIntoView?.({ block: "nearest", inline: "start" })
      else if (target && timeline)
        timeline.scrollLeft +=
          target.getBoundingClientRect().left - timeline.getBoundingClientRect().left
    }
  }
</script>

{#snippet calendarContent()}
  <div class="scheduling-calendar" bind:this={root}>
    {#if !presentation && !frame}
      <div
        class="controls"
        bind:clientHeight={controlsHeight}
        style:top={boardProps.stickyHeaderTop ?? "0px"}
      >
        <CalendarViewControls
          view={preferences.view}
          gridMinutes={preferences.gridMinutes}
          onViewChange={(view) => changePreferences({ view })}
          onGridChange={(gridMinutes) => changePreferences({ gridMinutes })}
        />
      </div>
    {/if}
    {#key `${displayView}:${displayGridMinutes}`}
      {#if displayView === "overview"}
        <CalendarOverview
          columns={balancedColumns}
          {visibleDays}
          {timezone}
          now={boardProps.now}
          actions={boardProps.actions}
          interaction={boardProps.interaction}
          readOnly={boardProps.readOnly}
          isLoading={boardProps.isLoading}
          editableEmployeeIds={boardProps.editableEmployeeIds}
          showUpdateSuccessNotification={boardProps.showUpdateSuccessNotification}
        />
      {:else}
        <BoardViewport
          {...boardProps}
          columns={displayColumns}
          layout={displayLayout}
          stickyHeaderTop={`calc(${boardProps.stickyHeaderTop ?? "0px"} + ${displayedControlsHeight}px)`}
          timeSlots={displaySlots}
          timeColumnHeight={displayHeight}
        />
      {/if}
    {/key}
  </div>
{/snippet}

{#if frame}
  {@render frame(displayControls, calendarContent)}
{:else}
  {@render calendarContent()}
{/if}

<style>
  .scheduling-calendar {
    /* Both consumers place the calendar on a light surface, even in the dark planning dialog. */
    --cal-bg: #ffffff;
    --cal-surface: #f3f5f6;
    --cal-border: #e6eaed;
    --cal-border-strong: #dce2e6;
    --cal-text: #2f4858;
    --cal-text-muted: #617481;
    --cal-accent: var(--primary-100, #00c95f);
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
    color: var(--cal-text);
    color-scheme: light;
  }
  .controls {
    position: sticky;
    z-index: 45;
    background: var(--cal-bg);
    border-bottom: 1px solid var(--cal-border);
  }
</style>
